import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './Brand';
import { LanguageSwitch, useI18n } from './i18n';
import SiteFooter from './SiteFooter';
import { FoundingStrip } from './Founding';
import { Icon } from './Icons';
import { FEATURED_FIRMS, FIRM_PAGES } from './firmPages';
import { EXPORTS } from './exportSteps';
import { trackClick } from './analytics';
import { money } from './Plans';
import { FREE_ACCOUNTS, FREE_FEATURES, PRICES, PRO_FEATURES, TRIAL_DAYS } from './planData';

// The firms named in the homepage's firm line; the rest are counted.
const FEATURED = FIRM_PAGES.filter((p) => FEATURED_FIRMS.includes(p.firm));


const WORKS_WITH = ['TopstepX live sync', 'Interactive Brokers sync', 'Tradovate', 'NinjaTrader', 'Any CSV'];


// Screenshots of the app, in English or Spanish to match the page. They're taken
// from the demo account, so every picture tells the same story.
function shot(name, lang) {
  return `/${name}${lang === 'es' ? '-es' : ''}.jpg`;
}

const SIZES = {
  dashboard: { en: [2880, 1800], es: [2880, 1800] },
  'tour-accounts': { en: [2880, 1800], es: [2880, 1800] },
  'tour-home': { en: [2880, 1800], es: [2880, 1800] },
  'tour-calendar': { en: [2880, 1800], es: [2880, 1800] },
  'tour-trades': { en: [2880, 1800], es: [2880, 1800] },
  'tour-insights': { en: [2880, 1800], es: [2880, 1800] },
  'tour-session': { en: [2880, 1800], es: [2880, 1800] },
  'home-rules': { en: [1584, 746], es: [1584, 872] },
  'home-insights': { en: [1984, 1214], es: [1984, 1296] },
  'home-session': { en: [1984, 1546], es: [1984, 1576] },
  'home-morning': { en: [1984, 2300], es: [1984, 2300] },
  'home-reflection': { en: [1984, 2300], es: [1984, 2300] },
  'screenshot-reset': { en: [1200, 944], es: [1200, 977] },
};

function image(name, lang, alt) {
  const [width, height] = SIZES[name][lang === 'es' ? 'es' : 'en'];
  return { src: shot(name, lang), width, height, alt };
}

// The two proof rows: account limits and patterns, each with a screenshot and a link into the demo.
// `demo` is where the demo opens: a path, or a function of the demo account.
const BENEFITS = [
  {
    id: 'limits',
    eyebrow: 'Your account limits',
    title: 'Know your limits before you hit them',
    text: "Pick your firm's preset and import your trades. The rules panel turns them into plain answers: how far you are from passing, from your max loss and from today's limit.",
    points: [
      'Daily loss limit, max loss and profit target in dollars, after every import',
      'End-of-day, intraday and static drawdown, including where it stops trailing',
      'Consistency rule and payout days, with a warning before they hold you back',
      'Presets for Topstep, Apex, MyFundedFutures, Lucid, Tradeify and TradeDay',
    ],
    shot: 'home-rules',
    alt: "Rules panel for a 50K evaluation: $1,808 from passing, $1,870 from the max loss, best day at 63% of profit against a 55% consistency limit, and $919 left before today's daily loss limit",
    demoLabel: 'See the rules panel in the demo',
    demo: '/app/dashboard',
  },
  {
    id: 'patterns',
    eyebrow: 'Your patterns',
    title: "See what's really costing you money",
    text: "Market Matrix reads your own trades and journal and tells you what's working, what's costing you and what to try next, in dollars.",
    points: [
      'Your best and worst times of day, weekdays, setups and hold times',
      'What happens after a loss, and on days you break your rules or skip your plan',
      'Mistake tags and trade grades, so you see which habits cost the most',
      'Plain findings, never guesses: every number comes from your trades',
    ],
    shot: 'home-insights',
    alt: "Insights showing what's working, what's costing you (including a 2:00 to 2:30 PM window with a 0% win rate) and what to try next",
    demoLabel: 'See the insights in the demo',
    demo: '/app/insights',
  },
];

// How Market Matrix handles members' data, for the trust section.
const SAFETY = [
  ['Read-only connections', 'TopstepX and Interactive Brokers sync only read your trades. Market Matrix never places, changes or cancels orders.'],
  ['Encrypted keys', 'Your broker API keys and tokens are stored encrypted.'],
  ['A protected account', 'Passwords are stored hashed, never in plain text, and you can turn on two-factor sign-in.'],
  ['Your data stays yours', 'We don’t sell your data and we don’t show ads. Download everything or delete your account at any time from Settings.'],
  ['Safe payments', 'Payments are handled by Paddle.com. We never see your card number.'],
];

// One trading day in three screens: the routine that sets Market Matrix apart, shown once.
const DAY = [
  { when: 'Before the open', title: 'Morning prep', text: "A check-in, your rules, yesterday's slip-ups as today's focus, and no P&L on screen.", shot: 'home-morning', alt: "Morning prep with yesterday's FOMO as today's focus, an emotional check-in, ranked rules and a morning checklist" },
  { when: 'During the session', title: 'Live session', text: 'Your plan on one calm screen, timeouts, a session log and reset buttons for FOMO, tilt and revenge.', shot: 'home-session', alt: "Live session with ranked trading rules, today's focus from yesterday's FOMO, and a time-stamped log that includes a FOMO reset button tap" },
  { when: 'After the close', title: 'End-of-day reflection', text: 'The rules you followed, the emotions that hit, and one thing to work on tomorrow.', shot: 'home-reflection', alt: 'End-of-day reflection for a tilt day: rules broken, session notes with a reset button tap, and what to work on tomorrow' },
];

// Real quotes from members, shown under the day timeline once there are some:
// { quote, name, account } with the member's permission. Empty hides the section.
const QUOTES = [];

const FAQ = [
  {
    q: 'Is Market Matrix free?',
    a: "Every new member gets 14 days of every feature, free, with no credit card. After that, the core journal stays free for good on one account: automatic TopstepX and Interactive Brokers sync or file imports, trade charts, your P&L calendar and stats, your prop firm rules panel and your daily routine. Pro adds unlimited accounts, Insights and the full Notebook, for $19 a month or $180 a year. You’ll never be charged unless you choose Pro.",
  },
  {
    q: 'What is the founding member offer?',
    a: "Founding member spots are limited to 50, and the countdown on this page shows how many are left. Creating a free account while spots are left is all it takes: your number and badge appear right away. Founding members get three things. First, 50% off any Market Matrix plan, at whatever it costs, for as long as their account stays open, including plans we add later (Pro is $9.50 a month instead of $19 today). The discount stays if they cancel and come back. Second, an exclusive numbered badge, from #1 to #50. Third, a voice in what we build: a private Founders Council on our Discord to suggest features, vote on priorities and talk directly with the creator. Joining the council is optional, and the badge and discount don't depend on it. Founders shape the roadmap, and we make the final decisions. Like everyone, founding members keep the core journal free for good.",
  },
  {
    q: 'My platform isn’t listed, or I need something special. Can you help?',
    a: 'Yes. We’re here to make your trading journey as easy and smooth as possible. Reach out on the <0>Help & contact page</0> and tell us your trading platform, and we’ll work with you to add it. We’re also happy to help with any other reasonable request.',
    link: '/contact',
  },
  {
    q: 'Does it connect to my broker or prop firm?',
    a: 'TopstepX, yes: connect it once with your TopstepX API Access (a separate TopstepX subscription) and your trades sync every 15 minutes, with your Topstep plan rules loaded automatically. It only reads your trades and never places orders. For Tradovate, NinjaTrader and other platforms, import a file or add trades by hand; your rules panel updates either way.',
  },
  {
    q: 'Can it stop me from breaking a rule?',
    a: "It can't block trades. It shows exactly how much room you have left and what's costing you, and the Live session screen keeps your rules, your daily stop and the reset buttons in front of you while you trade.",
  },
  {
    q: 'Which platforms and prop firms does it work with?',
    a: 'TopstepX, Tradovate and NinjaTrader web exports work as they are, and any CSV with a symbol, entry price and exit price can be imported. Rule presets cover Topstep, Apex Trader Funding, MyFundedFutures, Lucid Trading, Tradeify and TradeDay, and every number can be edited for any other firm.',
  },
  {
    q: 'Is my trading data private?',
    a: "Your journal is only visible to you. We don't sell your data or show ads, and you can download or delete everything in Settings at any time. <0>Read our Privacy Policy</0>.",
    link: '/privacy',
  },
  {
    q: 'Is it available in Spanish?',
    a: 'Yes. The whole site, the journal and the emails are in English and Spanish.',
  },
];

// A screenshot that opens full size when clicked, so its details are readable.
function Zoomable({ onOpen, ...img }) {
  const { t } = useI18n();
  return (
    <button type="button" className="zoomable" onClick={() => onOpen(img)} aria-label={t('Enlarge: {what}', { what: img.alt })}>
      <img {...img} alt={img.alt} />
      <span className="zoomable-hint" aria-hidden="true">{t('Click to enlarge')}</span>
    </button>
  );
}

function Lightbox({ image: img, onClose }) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);
  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={img.alt} onClick={onClose}>
      <button type="button" className="lightbox-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
      <img src={img.src} alt={img.alt} onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

// Opens the sample account. `to` is where it lands: a path, or a function of the demo account.
// `link` shows it as a text link instead of a button.
export function DemoButton({ inDemo, onStart, to = '/app/dashboard', label, link = false, track }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function open() {
    if (track) trackClick(track);
    setError('');
    setBusy(true);
    try {
      const demoUser = await onStart(lang);
      navigate(typeof to === 'function' ? to(demoUser || {}) : to);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const text = busy ? t('Opening the demo…') : label ? t(label) : inDemo ? t('Back to the demo') : t('View demo');
  return (
    <>
      <button type="button" className={link ? 'link-button demo-link' : 'secondary-cta demo-button'} onClick={open} disabled={busy}>
        {text}{link && !busy && <> &rarr;</>}
      </button>
      {error && <p className="error demo-error">{error}</p>}
    </>
  );
}

// The hero's account card: the Topstep 50K card from the Accounts page, with the demo account's
// numbers and the app's own styles and labels, so the first screen is exactly what members see.
const HERO_METERS = [
  { title: 'Max loss room', tag: 'OK', tagTone: 'good', value: '$1,869.80', of: '2K', fill: 0.93, tone: 'good', left: ['{p} left', { p: '93%' }], right: ['floor {amount}', { amount: '$49,321.80' }] },
  { title: 'Daily loss', tag: 'OK', tagTone: 'good', value: '$0.00', of: '1K', fill: 0, tone: 'good', left: ['{p} used today', { p: '0%' }], right: ['{amount} left', { amount: '$1,000.00' }] },
  { title: 'Profit target', tag: '40%', tagTone: 'target', value: '$1,191.60', of: '3K', fill: 0.4, tone: 'target', left: ['{amount} to go', { amount: '$1,808.40' }], right: '→ 53K' },
  { title: 'Consistency', tag: 'Over', tagTone: 'bad', value: '63%', fill: 1, tone: 'bad', left: ['cap {p}%', { p: 55 }], right: ['best day {amount}', { amount: '$751.80' }] },
];

function HeroAccountCard() {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const say = (x) => (Array.isArray(x) ? t(x[0], x[1]) : x);
  return (
    <figure className="hero-acct">
      <article className="guard-card" role="img"
        aria-label={t('Accounts page card for a Topstep 50K sample account: $1,869.80 of room to the max loss, $1,000.00 left before today’s daily loss limit, $1,808.40 to the profit target, and the best day at 63% of profit against a 55% consistency cap')}>
        <div className="guard-body">
          <div className="guard-id">
            <div className="guard-id-top">
              <span className="pf-badge pf-md pf-topstepx" aria-hidden="true">TX</span>
              <div className="acct-card-title">
                <span className="acct-name">Topstep 50K</span>
                <span className="acct-number">50KTC-V2-284716</span>
              </div>
              <span className="acct-pill pill-synced"><i aria-hidden="true" />{t('Live sync · Synced')}</span>
            </div>
            <div className="guard-chips">
              <span className="guard-chip hot">{t('Evaluation')}</span>
              <span className="guard-chip">50K</span>
              <span className="guard-chip">TopstepX</span>
            </div>
            <p className="guard-label">{t('Balance')}</p>
            <strong className="guard-balance">$51,191.60</strong>
            <span className="guard-change gain">+$1,191.60 {t('since start')} · {t('{n} trades', { n: 23 })}</span>
          </div>
          <div className="guard-meters">
            {HERO_METERS.map((m) => (
              <div className="guard-meter" key={m.title}>
                <div className="guard-meter-top"><span>{t(m.title)}</span><em className={`guard-tag tag-${m.tagTone}`}>{t(m.tag)}</em></div>
                <div className="guard-value"><strong>{m.value}</strong>{m.of && <span> / {m.of}</span>}</div>
                <div className={`acct-meter-bar tone-${m.tone}`}><span style={{ width: shown ? `${m.fill * 100}%` : 0 }} /></div>
                <div className="guard-meter-foot"><span>{say(m.left)}</span><span>{say(m.right)}</span></div>
              </div>
            ))}
          </div>
        </div>
        <p className="guard-tip tip-calm"><span aria-hidden="true">⚡</span> {t('{amount} left before today’s daily loss limit: about {n} average losses ({avg} each).', { amount: '$1,000.00', n: 6, avg: '$143.76' })}</p>
      </article>
      <figcaption className="rp-note">{t('Sample account. Every number comes from your own trades.')}</figcaption>
    </figure>
  );
}

function Landing({ user: signedIn, forceLang, onStartDemo }) {
  const { t, tr, lang, setLang } = useI18n();
  const location = useLocation();
  const [zoomed, setZoomed] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // A visitor exploring the demo still sees the page as a visitor.
  const inDemo = Boolean(signedIn && signedIn.isDemo);
  const user = inDemo ? null : signedIn;
  const showDemo = !user && Boolean(onStartDemo);
  const accountDeleted = Boolean(location.state && location.state.accountDeleted) && !user;
  const primaryCta = user
    ? { to: '/app', label: t('Open your journal') }
    : { to: '/signup', label: t('Start your free journal') };
  const local = (path) => `${lang === 'es' ? '/es' : ''}${path}`;

  // The Spanish homepage (/es) always opens in Spanish, and remembers it for the rest of the site.
  useEffect(() => {
    if (forceLang) setLang(forceLang);
  }, [forceLang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Links like /#features from other pages land on that section.
  useEffect(() => {
    if (!location.hash) return undefined;
    if (location.hash === '#export') setExportOpen(true);
    const id = setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    return () => clearTimeout(id);
  }, [location.hash]);

  function showExport(e) {
    e.preventDefault();
    setExportOpen(true);
    setTimeout(() => document.getElementById('export')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  const links = [
    { href: '#features', label: t('Features') },
    { href: '#your-day', label: t('How it works') },
    { to: local('/pricing'), label: t('Pricing') },
    { to: local('/tools'), label: t('Free tools') },
    { to: local('/blog'), label: t('Blog') },
  ];
  const navLinks = links.map((l) => (l.to
    ? <Link key={l.label} to={l.to} onClick={() => setMenuOpen(false)}>{l.label}</Link>
    : <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}>{l.label}</a>));

  return (
    <div className="landing">
      <nav className="landing-nav">
        <Link to="/" className="brand-home">
          <Logo />
          <div>
            <div className="brand-name">Market Matrix</div>
            <div className="brand-tag">{t('Trading Journal')}</div>
          </div>
        </Link>
        <div className="landing-nav-links">{navLinks}</div>
        <div className="landing-nav-end">
          <LanguageSwitch />
          {user ? (
            <Link to="/app" className="nav-button">{t('Open journal')}</Link>
          ) : (
            <>
              <Link to="/login" className="nav-login">{t('Log in')}</Link>
              <Link to="/signup" className="nav-button" onClick={() => trackClick('header-signup')}>{t('Start free')}</Link>
            </>
          )}
          <button type="button" className="nav-menu-button" aria-expanded={menuOpen} aria-controls="landing-menu" aria-label={t('Menu')} onClick={() => setMenuOpen((o) => !o)}>
            <Icon name={menuOpen ? 'close' : 'menu'} />
          </button>
        </div>
        {menuOpen && (
          <div className="landing-menu" id="landing-menu">
            {navLinks}
            {!user && <Link to="/login" onClick={() => setMenuOpen(false)}>{t('Log in')}</Link>}
            <LanguageSwitch />
          </div>
        )}
      </nav>

      {accountDeleted && (
        <p className="landing-notice" role="status">{t('Your account and all of its data have been deleted. Thanks for trying Market Matrix.')}</p>
      )}

      <header className="hero hero-split">
        <div className="hero-copy">
          <p className="eyebrow">{t('Discipline · Account limits · Insights')}</p>
          <h1>
            {tr('The trading journal <0>built for prop traders.</0>', [(x) => <span className="hero-accent">{x}</span>])}
          </h1>
          <p className="hero-subtitle">
            {t('Protect your funded account, stick to your plan and find the habits that cost you money, with your prop firm’s rules tracked from your own trades.')}
          </p>
          <div className="hero-buttons">
            <Link to={primaryCta.to} className="cta-button" onClick={() => trackClick('hero-signup')}>{primaryCta.label} &rarr;</Link>
            {showDemo && <DemoButton inDemo={inDemo} onStart={onStartDemo} track="hero-demo" />}
          </div>
          {!user && <p className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>}
          <p className="hero-ready">
            {t('Not ready yet?')}{' '}
            <Link to={local('/report-card')} onClick={() => trackClick('hero-report-card')}>{t('Get a free grade on your trades. No sign-up.')} &rarr;</Link>
          </p>
        </div>
        <div className="hero-visual">
          <HeroAccountCard />
        </div>
      </header>

      {!user && <FoundingStrip />}

      <section className="works-band" aria-label={t('Works with')}>
        <ul className="works-with">
          {WORKS_WITH.map((w) => <li key={w}>{t(w)}</li>)}
        </ul>
        <p className="firm-list">
          {t('Rules built in for')}{' '}
          <span>
            {FEATURED.map((p, i) => (
              <span key={p.slug}>{i > 0 && ', '}<Link to={local(`/${p.slug}`)}>{p.fullName || p.name}</Link></span>
            ))}
          </span>
          {' '}{t('and {n} more prop firms', { n: FIRM_PAGES.length - FEATURED.length })}
          {' · '}<Link to={local('/prop-firms')}>{t('See all firms')}</Link>
          {' · '}<a href="#export" onClick={showExport}>{t('How to export your trades')}</a>
        </p>
      </section>

      <div className="landing-rest">
      <section className="landing-section journal-shot" aria-labelledby="journal-shot-title">
        <p className="eyebrow">{t('Inside Market Matrix')}</p>
        <h2 className="section-title" id="journal-shot-title">{t('Every account. Every rule. One screen.')}</h2>
        <p className="section-sub">{t('Your balance, how far you are from passing and from your max loss, your consistency rule and your stats, updated after every sync or import.')}</p>
        <div className="journal-shot-frame">
          <Zoomable onOpen={setZoomed} loading="lazy"
            {...image('dashboard', lang, t("The Market Matrix dashboard for a 50K evaluation: the left menu with Home and the Journal pages, the account balance, distance to the profit target and max loss, the consistency rule, today's loss limit, and stat tiles for net P&L, win rate and profit factor"))} />
        </div>
        {showDemo && <p className="journal-shot-demo"><DemoButton inDemo={inDemo} onStart={onStartDemo} label="Open the Dashboard in the demo" link track="dashboard-demo" /></p>}
      </section>

      <section className="landing-section day-section" id="your-day">
        <div className="day-head">
          <div>
            <p className="eyebrow">{t('How it works')}</p>
            <h2 className="section-title">{t('One trading day. Three screens.')}</h2>
            <p className="section-sub">{t("Most accounts aren't blown by bad setups. They're blown by tilt, FOMO and skipped rules. Market Matrix is with you before, during and after every session.")}</p>
          </div>
          {showDemo && (
            <DemoButton inDemo={inDemo} onStart={onStartDemo} label="See the tilt day in the demo" track="day-demo"
              to={(demoUser) => (demoUser.demo && demoUser.demo.tiltDate ? `/app/reflection?date=${demoUser.demo.tiltDate}` : '/app/reflection')} />
          )}
        </div>
        <ol className="day-steps">
          {DAY.map((d, i) => (
            <li key={d.title}>
              <span className="day-when"><span className="day-number">{i + 1}</span>{t(d.when)}</span>
              <h3>{t(d.title)}</h3>
              <p>{t(d.text)}</p>
              <div className="day-shot">
                <Zoomable onOpen={setZoomed} loading="lazy" {...image(d.shot, lang, t(d.alt))} />
              </div>
            </li>
          ))}
        </ol>
      </section>

      {QUOTES.length > 0 && (
        <section className="landing-section quotes" aria-label={t('What traders say')}>
          {QUOTES.map((q) => (
            <figure key={q.name}>
              <blockquote>“{t(q.quote)}”</blockquote>
              <figcaption>{q.name} · {q.account}</figcaption>
            </figure>
          ))}
        </section>
      )}

      <section className="landing-section benefits" id="features">
        {BENEFITS.map((b, i) => (
          <article key={b.id} className={`benefit benefit-${b.id}${i % 2 ? ' benefit-flip' : ''}`}>
            <div className="benefit-text">
              <p className="eyebrow benefit-eyebrow">{t(b.eyebrow)}</p>
              <h3>{t(b.title)}</h3>
              <p className="benefit-lead">{t(b.text)}</p>
              <ul className="benefit-points">
                {b.points.map((pt) => <li key={pt}>{t(pt)}</li>)}
              </ul>
              {showDemo && <DemoButton inDemo={inDemo} onStart={onStartDemo} to={b.demo} label={b.demoLabel} link track="benefit-demo" />}
            </div>
            <div className="benefit-media">
              <div className="benefit-shot">
                <Zoomable onOpen={setZoomed} loading="lazy" {...image(b.shot, lang, t(b.alt))} />
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="landing-section trust" id="trust">
        <p className="eyebrow">{t('Who’s behind it')}</p>
        <h2 className="section-title">{t('Your trades are in good hands')}</h2>
        <div className="trust-grid">
          <figure className="trust-founder">
            <blockquote>
              <p>{t('We built Market Matrix to be more than a P&L tracker: a journal that watches your prop firm’s rules and helps you stick to your plan on the days it’s hardest.')}</p>
              <p>{t('We read every message and every piece of feedback, and many features on this page started as one. If something is missing for your firm or platform, tell us.')}</p>
            </blockquote>
            <figcaption>— {t('The Market Matrix team')} · <Link to="/contact">{t('Write to us')} &rarr;</Link></figcaption>
          </figure>
          <div className="trust-safety">
            <h3>{t('How we handle your data')}</h3>
            <ul>
              {SAFETY.map(([title, text]) => <li key={title}><strong>{t(title)}.</strong> {t(text)}</li>)}
            </ul>
            <Link to="/privacy" className="trust-link">{t('Read the Privacy Policy')} &rarr;</Link>
          </div>
        </div>
      </section>

      <section className="landing-section home-pricing" id="pricing">
        <p className="eyebrow">{t('Pricing')}</p>
        <h2 className="section-title">{t('Start free. Upgrade when you outgrow it.')}</h2>
        <p className="section-sub">{t('Get every feature free for {n} days, no credit card. After your trial, keep the core journal for free or upgrade to Pro.', { n: TRIAL_DAYS })}</p>
        <div className="plans-grid">
          <section className="plan-card">
            <h3>{t('Free')}</h3>
            <p className="plan-price"><strong>$0</strong></p>
            <p className="field-hint">{t('The core journal, free for good.')}</p>
            <ul>{FREE_FEATURES.map((f) => <li key={f}>{t(f, { n: FREE_ACCOUNTS })}</li>)}</ul>
          </section>
          <section className="plan-card pro">
            <h3>{t('Pro')}</h3>
            <p className="plan-price"><strong>{money(PRICES.month)}</strong> <span>{t('a month')}</span></p>
            <p className="field-hint">{t('or {price} a year', { price: money(PRICES.year) })}</p>
            <ul>{PRO_FEATURES.map((f) => <li key={f}>{t(f)}</li>)}</ul>
          </section>
        </div>
        <p className="pricing-founding">★ {t('Founding members get 50% off any plan for as long as their account stays open: {month} a month or {year} a year for Pro today.', { month: money(PRICES.month / 2), year: money(PRICES.year / 2) })}</p>
        <p className="features-more"><Link to={local('/pricing')} className="secondary-cta">{t('See full pricing and questions')} &rarr;</Link></p>
      </section>

      <section className="landing-section faq" id="faq">
        <p className="eyebrow">{t('FAQ')}</p>
        <h2 className="section-title">{t('Questions traders ask')}</h2>
        <div className="faq-list">
          {FAQ.map((f) => (
            <details key={f.q} className="faq-item">
              <summary>{t(f.q)}</summary>
              <p>{f.link ? tr(f.a, [(x) => <Link to={f.link}>{x}</Link>]) : t(f.a)}</p>
            </details>
          ))}
        </div>
        <div className="export-details" id="export">
          <button type="button" className="export-toggle" aria-expanded={exportOpen} onClick={() => setExportOpen((o) => !o)}>
            <span>{t('How to export your trades from TopstepX, Tradovate, NinjaTrader or any platform')}</span>
            <span className="export-toggle-icon" aria-hidden="true">{exportOpen ? '−' : '+'}</span>
          </button>
          {exportOpen && (
            <>
              <div className="export-guides">
                {EXPORTS.map((x) => (
                  <div key={x.name} className="export-guide">
                    <h3>{t(x.name)}</h3>
                    <ol>
                      {x.steps.map((st) => <li key={st}>{t(st)}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
              <p className="export-note">
                {tr('Menus can change when platforms update. Stuck? <0>Send us a message</0> and we’ll help you find it.', [(x) => <Link to="/contact">{x}</Link>])}
              </p>
            </>
          )}
        </div>
      </section>

      <section className="final-cta">
        <h2>{t('Trade your plan. Keep your account.')}</h2>
        <p>{t('Connect TopstepX or Interactive Brokers, or import a file, and see exactly where you stand in minutes.')}</p>
        <div className="hero-buttons">
          <Link to={primaryCta.to} className="cta-button" onClick={() => trackClick('final-signup')}>{primaryCta.label} &rarr;</Link>
          {showDemo && <DemoButton inDemo={inDemo} onStart={onStartDemo} track="final-demo" />}
        </div>
        {!user && <p className="free-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>}
      </section>

      <SiteFooter />
      </div>
      {zoomed && <Lightbox image={zoomed} onClose={() => setZoomed(null)} />}
    </div>
  );
}

export default Landing;
